import { IDashboardState } from '@/modules/dashboard/store/state';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { DashboardService } from '@/modules/dashboard/services';
import * as AT from './actionTypes';
import * as MT from './mutationsTypes';
import Organisation from '@/models/Organisation';
import OrganisationType from '@/models/OrganisationType';
import User from '@/models/User';
import { UpdateUserSettingsRequest, Google2faDetails } from '../types';
import { Currency } from '@/modules/fund/types';
import { FundService } from '@/modules/fund/services';

/**
 * --------------------------------------------------
 *  Dashboard Actions
 * --------------------------------------------------
 **/
export const DashboardActions: IDashboardActions = {
  [AT.FETCH_ORGANISATIONS]: ({ commit }): Promise<Organisation[]> => {
    return DashboardService.getOrganisations().then(
      (organisations: Organisation[]) => {
        commit(MT.SET_ORGANISATIONS, organisations);
        return organisations;
      }
    );
  },
  [AT.FETCH_ORGANISATION_TYPES]: ({ commit }): Promise<OrganisationType[]> => {
    return DashboardService.getOrganisationTypes().then(
      (organisationTypes: OrganisationType[]) => {
        commit(MT.SET_ORGANISATION_TYPES, organisationTypes);
        return organisationTypes;
      }
    );
  },
  [AT.FETCH_CURRENT_USER]: async ({ commit }): Promise<User> => {
    const user = await DashboardService.fetchCurrentUser();
    commit(MT.SET_CURRENT_USER, user);
    return user;
  },
  [AT.FETCH_CURRENCIES]: async ({ commit }): Promise<Currency[]> => {
    const isMultiCurrencyEnabled =
      process.env.VUE_APP_MULTI_CURRENCY_ADHOC_DATAPOINTS === 'enabled';
    return FundService.getCurrencies().then((currencies: Currency[]) => {
      // If Multi-Currency is not enabled for Adhoc datapoints,
      // Just return GBP as option.
      if (!isMultiCurrencyEnabled) {
        const filteredCurrencies = currencies?.filter(c => c?.slug === 'GBP');
        commit(MT.SET_CURRENCIES, filteredCurrencies);
        return currencies;
      }
      commit(MT.SET_CURRENCIES, currencies);
      return currencies;
    });
  },
  [AT.EDIT_USER_DETAILS]: async (
    { commit },
    data: UpdateUserSettingsRequest
  ): Promise<User> => {
    const user = await DashboardService.editUserDetails(data);
    commit(MT.SET_CURRENT_USER, user);
    return user;
  },

  [AT.SETUP_GOOGLE2FA]: async (
    { commit },
    userId: string
  ): Promise<Google2faDetails> => {
    const googleResponse = await DashboardService.setupGoogle2fa(userId);
    return googleResponse;
  }
};

/**
 * --------------------------------------------------
 *  Dashboard Action Context Interface
 * --------------------------------------------------
 **/
export interface IDashboardActionContext
  extends ActionContext<IDashboardState, IRootState> {}

/**
 * --------------------------------------------------
 *  Dashboard Actions Interface
 * --------------------------------------------------
 **/
export interface IDashboardActions
  extends ActionTree<IDashboardState, IRootState> {
  [AT.FETCH_ORGANISATIONS](
    context: IDashboardActionContext
  ): Promise<Organisation[]>;
  [AT.FETCH_ORGANISATION_TYPES](
    context: IDashboardActionContext
  ): Promise<OrganisationType[]>;
  [AT.FETCH_CURRENT_USER](context: IDashboardActionContext): Promise<User>;
  [AT.SETUP_GOOGLE2FA](
    context: IDashboardActionContext,
    userId: string
  ): Promise<Google2faDetails>;
  [AT.FETCH_CURRENCIES](context: IDashboardActionContext): Promise<Currency[]>;
}
